import { memo } from 'react';
import clsx from 'clsx';

import { Image, Loader } from '@highlight-cp/core-ui';
import type { ImageProps } from '@highlight-cp/core-ui';

import classes from './image-with-loader.module.css';

const DEFAULT_WIDTH = 90;
const DEFAULT_HEIGHT = 90;

// v7 ImageProps no longer carries native <img> attributes (alt, src, …) — intersect them back in.
interface ImageWithLoaderProps
  extends
    ImageProps,
    Omit<React.ComponentPropsWithoutRef<'img'>, keyof ImageProps | 'width' | 'height'> {
  isLoading: boolean;
  /** Lets the wrapper span the parent width instead of shrink-to-fit (needed when centering in flex layouts). */
  fillParentWidth?: boolean;
  /** Rendered size in px (v7 Image dropped the dedicated props; mapped to `w`/`h`). */
  width?: number;
  height?: number;
}

/**
 * `ImageWithLoader`
 * ---
 * @description
 * A component that displays an image with a loader when the image is loading.
 * @example
 * <ImageWithLoader src="https://via.placeholder.com/150" isLoading={true} />
 * @param {string} src - The source URL of the image.
 * @param {boolean} isLoading - Whether the image is loading.
 * @param {number} width - The width of the image.
 * @param {number} height - The height of the image.
 * @param {...ImageProps} props - Additional props to pass to the `Image` component.
 * @returns {JSX.Element}
 */
const ImageWithLoader = memo(
  ({
    src,
    isLoading,
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
    fillParentWidth = false,
    ...props
  }: ImageWithLoaderProps) => {
    return (
      <div
        className={clsx(
          classes.container,
          fillParentWidth && classes.fillParentWidth,
          isLoading && classes.dashedBorder,
        )}
      >
        <Image w={width} h={height} src={src} {...props} />
        {isLoading && <Loader className={classes.loader} />}
      </div>
    );
  },
);

ImageWithLoader.displayName = 'ImageWithLoader';

export default ImageWithLoader;
